import { DependencyList, useCallback, useEffect, useRef, useState } from 'react';

type RefCallback<T> = (node: T | null) => void;
type CssPropertySetter = (property: number) => void;

/**
 * Lees een CSS Dynamic Property uit.
 * (let op: Op dit moment worden alleen numbers ondersteund. dus geen complexe CSS values zoals '1px 2px blue')
 *
 * Het eerste element uit de resultaat-array is de propValue is de waarde uit de CSS definities.
 *
 * Het tweede element uit de resultaat-array moet worden gekoppeld aan het element waarop
 * je de waarde wilt opvragen, koppel deze via het speciale ref-attribuut aan het HTML-Element.
 *
 * ```ts
 * const [myPropValue, myElementRef] = useCssPropertyValue('--my-custom-property-name', [dep]);
 * ```
 */
function useReadCssProperty(property: string, fallback = -1, deps: DependencyList): [number, RefCallback<HTMLElement>] {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const fallbackVal = useRef<number | undefined>(undefined);
  const [propertyValue, setPropertyValue] = useState<number>(fallback);
  fallbackVal.current = fallback;

  useEffect(() => {
    if (ref) {
      const valueFromCSS = getPropertyValue(ref, property);
      setPropertyValue(valueFromCSS || fallbackVal.current!);
    }
  }, [ref, property, fallback, ...deps]);

  return [propertyValue, setRef];
}

/**
 * Pas de waarde aan van een CSS Dynamic Property.
 * (Let op: op dit moment worden alleen numbers ondersteund)
 */
function useWriteCssProperty(property: string): [CssPropertySetter, RefCallback<HTMLElement>] {
  const [ref, setRef] = useState<HTMLElement | null>(null);

  const setCssProperty: CssPropertySetter = useCallback(
    (newValue: number) => {
      if (ref) {
        ref.style.setProperty(property, `${newValue}`);
      }
    },
    [property, ref],
  );

  return [setCssProperty, setRef];
}

/**
 * Pas de waarde aan van een CSS Dynamic Property.
 * (Let op: op dit moment worden alleen numbers ondersteund)
 */
function useWriteAndReadCssProperty(
  property: string,
): [
  (property: number) => void,
  (value: ((prevState: HTMLElement | null) => HTMLElement | null) | HTMLElement | null) => void,
  number | undefined,
] {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState<number>();

  const setCssProperty: CssPropertySetter = useCallback(
    (newValue: number) => {
      setValue(newValue);
      if (ref) {
        ref.style.setProperty(property, `${newValue}`);
      }
    },
    [property, ref],
  );

  return [setCssProperty, setRef, value];
}

function getPropertyValue(elem: HTMLElement, cssDynamicPropertyName: string): number {
  const computedStyle = getComputedStyle(elem);
  const value = computedStyle.getPropertyValue(cssDynamicPropertyName.trim());
  return Number(value);
}

export { useReadCssProperty, useWriteAndReadCssProperty, useWriteCssProperty };
